// Implied volatility for the Option Chain (UI/UX audit Phase 4). Display only - no order, ranking or exit reads it.
// Black-76 on the underlying the options settle against (MCX: the future; NFO: the index / stock price shown),
// with r = 0: over days-to-weeks the rate term moves IV by far less than the bid/ask spread does.

const SQRT2 = Math.SQRT2;

// Abramowitz-Stegun 7.1.26 erf (max error ~1.5e-7) -> standard normal CDF
function cdf(x) {
  const t = 1 / (1 + 0.3275911 * Math.abs(x) / SQRT2);
  const y = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t
    * Math.exp(-(x * x) / 2);
  return x >= 0 ? 0.5 * (1 + y) : 0.5 * (1 - y);
}

/** Black-76 premium (r = 0) of a call (isCall) or put; F underlying, K strike, T years, sigma annual vol. */
export function black76(isCall, F, K, T, sigma) {
  if (!(F > 0) || !(K > 0) || !(T > 0) || !(sigma > 0)) return null;
  const v = sigma * Math.sqrt(T);
  const d1 = (Math.log(F / K) + (v * v) / 2) / v;
  const d2 = d1 - v;
  return isCall ? F * cdf(d1) - K * cdf(d2) : K * cdf(-d2) - F * cdf(-d1);
}

/** Annualised IV (0.18 = 18%) that prices `premium`, or null when the price is below intrinsic, the inputs are
 *  missing, or no volatility up to 500% fits. Bisection: monotone in sigma, always converges. */
export function impliedVol(isCall, premium, F, K, T) {
  const p = Number(premium);
  if (!(p > 0) || !(F > 0) || !(K > 0) || !(T > 0)) return null;
  const intrinsic = Math.max(0, isCall ? F - K : K - F);
  if (p <= intrinsic + 1e-9) return null;
  let lo = 1e-4, hi = 5;
  if (black76(isCall, F, K, T, hi) < p) return null;
  for (let i = 0; i < 80; i += 1) {
    const mid = (lo + hi) / 2;
    if (black76(isCall, F, K, T, mid) > p) hi = mid; else lo = mid;
  }
  return (lo + hi) / 2;
}

/** Years from `now` to the expiry session close (IST): NFO 15:30, MCX 23:30. null if already past. */
export function yearsToExpiry(expiry, market, now = new Date()) {
  if (!expiry) return null;
  const close = String(market || "").toUpperCase().startsWith("MCX") ? "23:30" : "15:30";
  const end = new Date(`${String(expiry).slice(0, 10)}T${close}:00+05:30`);
  const ms = end.getTime() - now.getTime();
  return ms > 0 ? ms / (365 * 86400 * 1000) : null;
}

/** The price an IV is read from: the bid/ask mid when both sides quote, else the LTP. */
export function ivPrice(s) {
  if (!s) return null;
  const b = Number(s.bid), a = Number(s.ask);
  return b > 0 && a > 0 && a >= b ? (a + b) / 2 : (Number(s.ltp) > 0 ? Number(s.ltp) : null);
}

/** MODEL Greeks of ONE option (Black-76, r = 0) at volatility `sigma`, per 1 unit long: delta, gamma, vega per 1 vol
 *  point, theta per calendar day. Display only (Common Trade Panel > Analyse > Greeks); null when inputs are missing. */
export function greeks(isCall, F, K, T, sigma) {
  if (!(F > 0) || !(K > 0) || !(T > 0) || !(sigma > 0)) return null;
  const v = sigma * Math.sqrt(T);
  const d1 = (Math.log(F / K) + (v * v) / 2) / v;
  const pdf = Math.exp(-(d1 * d1) / 2) / Math.sqrt(2 * Math.PI);
  return {
    delta: isCall ? cdf(d1) : cdf(d1) - 1,
    gamma: pdf / (F * v),
    vega: (F * pdf * Math.sqrt(T)) / 100,
    theta: -(F * pdf * sigma) / (2 * Math.sqrt(T)) / 365,
  };
}
